import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Phone, 
  Settings, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Clock,
  Smartphone
} from 'lucide-react';

const GestionSMS = () => {
  const [smsConfig, setSmsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formData, setFormData] = useState({
    account_sid: '',
    auth_token: '',
    from_phone: '',
    nombre_institucion: 'Instituto Bíblico Apostólico del Uruguay',
    modo_simulacion: true
  });
  const [testPhone, setTestPhone] = useState('+1-347-223-9226');
  const [logs, setLogs] = useState([]);

  const API = window.location.origin;

  useEffect(() => {
    loadSMSConfig();
    loadSMSLogs();
  }, []);

  const loadSMSConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/sms/config`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const config = await response.json();
        setSmsConfig(config);
        
        if (config.configurado) {
          setFormData({
            account_sid: '', // No mostrar por seguridad
            auth_token: '', // No mostrar por seguridad  
            from_phone: config.from_phone || '',
            nombre_institucion: config.nombre_institucion || 'Instituto Bíblico Apostólico del Uruguay',
            modo_simulacion: config.modo_simulacion || false
          });
        }
      }
    } catch (error) {
      console.error('Error loading SMS config:', error);
      toast.error('Error al cargar configuración SMS');
    } finally {
      setLoading(false);
    }
  };

  const loadSMSLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/admin/sms/logs?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error loading SMS logs:', error);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/admin/sms/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('✅ Configuración SMS guardada exitosamente');
        loadSMSConfig();
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error saving SMS config:', error);
      toast.error('Error al guardar configuración SMS');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    try {
      setTesting(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/admin/sms/test-specific`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          telefono: testPhone
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`✅ SMS de prueba enviado (${result.simulado ? 'Simulado' : 'Real'})`);
        loadSMSLogs(); // Recargar logs
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error testing SMS:', error);
      toast.error('Error al enviar SMS de prueba');
    } finally {
      setTesting(false);
    }
  };

  const getStatusBadge = (estado) => {
    switch (estado) {
      case 'enviado':
        return <Badge className="bg-green-500">Enviado</Badge>;
      case 'simulado':
        return <Badge className="bg-blue-500">Simulado</Badge>;
      case 'fallido':
        return <Badge className="bg-red-500">Fallido</Badge>;
      default:
        return <Badge className="bg-gray-500">Pendiente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-center">
          <Smartphone className="h-12 w-12 mx-auto mb-4 text-blue-500 animate-pulse" />
          <p>Cargando configuración SMS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-6 w-6 text-blue-500" />
          <h1 className="text-2xl font-bold">Gestión de SMS</h1>
        </div>
        
        {smsConfig && (
          <Badge className={smsConfig.configurado ? "bg-green-500" : "bg-orange-500"}>
            {smsConfig.configurado ? 'Configurado' : 'Modo Simulado'}
          </Badge>
        )}
      </div>

      <Tabs defaultValue="config" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="config" className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Configuración</span>
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center space-x-2">
            <Send className="h-4 w-4" />
            <span>Pruebas</span>
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <span>Historial</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Phone className="h-5 w-5" />
                <span>Configuración Twilio</span>
              </CardTitle>
              <p className="text-sm text-gray-600">
                Configure las credenciales de Twilio para enviar SMS reales. 
                Sin configuración, el sistema funciona en modo simulado.
              </p>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {smsConfig && !smsConfig.configurado && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center space-x-2 text-orange-800">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">Modo Simulado Activo</span>
                  </div>
                  <p className="text-sm text-orange-700 mt-2">
                    Los SMS se simularán y aparecerán en los logs. Configure Twilio para envío real.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_sid">Account SID</Label>
                  <Input
                    id="account_sid"
                    type="password"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={formData.account_sid}
                    onChange={(e) => setFormData({...formData, account_sid: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">Obténlo desde Twilio Console</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="auth_token">Auth Token</Label>
                  <Input
                    id="auth_token"
                    type="password"
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    value={formData.auth_token}
                    onChange={(e) => setFormData({...formData, auth_token: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">Token de autenticación de Twilio</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="from_phone">Número Twilio</Label>
                  <Input
                    id="from_phone"
                    placeholder="+1234567890"
                    value={formData.from_phone}
                    onChange={(e) => setFormData({...formData, from_phone: e.target.value})}
                  />
                  <p className="text-xs text-gray-500">Número asignado por Twilio</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nombre_institucion">Nombre Institución</Label>
                  <Input
                    id="nombre_institucion"
                    value={formData.nombre_institucion}
                    onChange={(e) => setFormData({...formData, nombre_institucion: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="modo_simulacion">Modo Simulación</Label>
                  <p className="text-sm text-gray-600">
                    Si está activado, los SMS se simularán aunque tengas credenciales
                  </p>
                </div>
                <Switch
                  id="modo_simulacion"
                  checked={formData.modo_simulacion}
                  onCheckedChange={(checked) => setFormData({...formData, modo_simulacion: checked})}
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveConfig} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar Configuración'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Probar SMS</CardTitle>
              <p className="text-sm text-gray-600">
                Envía un SMS de prueba para verificar la configuración
              </p>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test_phone">Número de Teléfono</Label>
                <Input
                  id="test_phone"
                  placeholder="+1234567890"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                />
              </div>

              <Button 
                onClick={handleTestSMS} 
                disabled={testing || !testPhone}
                className="w-full"
              >
                {testing ? 'Enviando...' : 'Enviar SMS de Prueba'}
              </Button>

              {smsConfig && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Estado actual:</strong> {smsConfig.configurado ? 'Twilio configurado' : 'Modo simulado'}
                    {smsConfig.modo_simulacion && ' (Simulación activada)'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Historial de SMS</CardTitle>
              <p className="text-sm text-gray-600">
                Últimos 20 SMS enviados (reales y simulados)
              </p>
            </CardHeader>
            
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No hay SMS en el historial</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log, index) => (
                    <div key={log.id || index} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{log.telefono_destinatario}</span>
                          {getStatusBadge(log.estado)}
                        </div>
                        <span className="text-xs text-gray-500">
                          {new Date(log.fecha_creacion).toLocaleString('es-UY')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 truncate">
                        {log.mensaje}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GestionSMS;